/**
 * フロー図に載せる差分の形。図の中身が何であっても同じ形で受け渡す。
 *
 * 業務フローと業務ロジックのフローでは、節点が指すもの（業務 / 処理）も
 * 枝が意味するもの（遷移 / 出力の値）も違う。それでも図の上で差分が表すことは 3 つしかない —
 * **節点の状態**（増えた / 変わった / 消えた）・**増えた枝**・**消えた枝**である。枠と図は
 * この 3 つだけを知り、何を突き合わせて得た差分かは知らない。
 *
 * 語彙と色は一覧・表と同じ 1 組（`SpecDiffState`）から引く。図だけが独自の色分けを持つと、
 * 同じ「追加」が画面によって違う色で出て、読み手は画面ごとに凡例を覚え直すことになる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図に載せる差分（節点の状態・増えた枝・消えた枝）を、図の中身に依らない 1 つの形で表す。
 * @graph-connects none
 */
import type { SpecDiffState } from '../../components/spec/SpecDiffState.js';

/**
 * 図の上に重ねる節点 1 個の差分状態。
 *
 * 状態の並びは `SpecDiffState` が持つ 1 組から引く。図は「変更なし」の印を描かない
 * （印が無いことがそのまま変更なしを表す）ので、そこだけ除く。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の上に重ねる節点 1 個の差分状態を、変更なしを除いた 1 組で表す。
 * @graph-connects none
 */
export type FlowChangeState = Exclude<SpecDiffState, 'unchanged'>;

/**
 * 追加された矢印に載せる class 名。見た目は `styles.css` が持つ。
 *
 * 色と線種を CSS に置くのは、React Flow が線そのものを SVG の `path` として自分で描き、その
 * 要素へスタイルを直接渡す口が無いためである。同じ綴りが CSS 側にも現れる。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 追加された矢印に載せる class 名を 1 箇所で持つ。
 * @graph-connects none
 */
export const ADDED_EDGE_CLASS = 'flow-edge-added';

/**
 * 削除された矢印に載せる class 名。見た目は `styles.css` が持つ。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 削除された矢印に載せる class 名を 1 箇所で持つ。
 * @graph-connects none
 */
export const REMOVED_EDGE_CLASS = 'flow-edge-removed';

/**
 * 差分を重ねない図で使う空の差分表。描画のたびに参照が変わらないよう 1 つを共有する。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分を重ねない図が使う、参照の変わらない空の節点状態表を与える。
 * @graph-connects none
 */
export const NO_CHANGE_STATES: ReadonlyMap<string, FlowChangeState> = new Map();

/**
 * 差分を重ねない図で使う空の遷移の印。描画のたびに参照が変わらないよう 1 つを共有する。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 差分を重ねない図が使う、参照の変わらない空の枝の印を与える。
 * @graph-connects none
 */
export const NO_MARKED_TRANSITIONS: ReadonlySet<string> = new Set();

/**
 * 図の面。変更前と変更後を並べるときに、どちらの印を載せるかを決める。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の面（変更前 / 変更後）を表す。
 * @graph-connects none
 */
export type FlowPaneSide = 'before' | 'after';

/**
 * 変更前と変更後の 2 枚に載せる差分。
 *
 * 枠はこの形だけを知り、突き合わせの中身（骨格の比較なのか、処理の並びの比較なのか）は
 * 使う側が持つ。件数や理由のような画面ごとの値は、この形を広げた型が足す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の 2 枚に載せる差分（節点の状態・増えた枝・消えた枝）を表す。
 * @graph-connects none
 */
export interface FlowCompareDiff {
  /** 変更前の図に載せる状態。削除された全節点と、中身が変わった節点を持つ。 */
  beforeStateByNodeId: ReadonlyMap<string, FlowChangeState>;
  /** 変更後の図に載せる状態。追加された全節点と、中身が変わった節点を持つ。 */
  afterStateByNodeId: ReadonlyMap<string, FlowChangeState>;
  /** 変更前にしか無い枝の ID。変更前の図で破線にする。 */
  removedTransitionIds: ReadonlySet<string>;
  /** 変更後にしか無い枝の ID。変更後の図で緑の実線にする。 */
  addedTransitionIds: ReadonlySet<string>;
}

/**
 * 図 1 枚に載せる印。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図 1 枚に載せる節点の状態と枝の印を表す。
 * @graph-connects none
 */
export interface FlowSideMarks {
  /** 節点に載せる差分の状態。差分を重ねないときは `NO_CHANGE_STATES`。 */
  stateByNodeId: ReadonlyMap<string, FlowChangeState>;
  /** 印を付ける枝の ID。重ねないときは `NO_MARKED_TRANSITIONS`。 */
  markedTransitionIds: ReadonlySet<string>;
  /** 印を付ける枝の class 名。 */
  markedClassName: string;
}

/**
 * 2 枚ぶんの差分から、片面に載せる印を取り出す。
 *
 * **面ごとに載せる印は決まっている。** 変更前の図に出せるのは消えた枝だけで、変更後の図に
 * 出せるのは増えた枝だけである。消えた枝は変更後の図に居ないので置く場所も繋ぐ相手も無い。
 * 面と印の対応を使う側で書くと、左右を取り違えた図（消えた枝が変更後に緑で出る）を作れる。
 *
 * @param diff 変更前と変更後の 2 枚ぶんの差分
 * @param side 印を取り出す面
 * @returns その面の図へそのまま渡す印
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 2 枚ぶんの差分から、変更前なら消えた枝・変更後なら増えた枝の印を取り出す。
 * @graph-connects none
 */
export function flowSideMarksOf(diff: FlowCompareDiff, side: FlowPaneSide): FlowSideMarks {
  if (side === 'before') {
    return {
      stateByNodeId: diff.beforeStateByNodeId,
      markedTransitionIds: diff.removedTransitionIds,
      markedClassName: REMOVED_EDGE_CLASS,
    };
  }

  return {
    stateByNodeId: diff.afterStateByNodeId,
    markedTransitionIds: diff.addedTransitionIds,
    markedClassName: ADDED_EDGE_CLASS,
  };
}
